import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NEW = "__new";

export const useLocations = () => {
  const [locations, setLocations] = useState([]);
  const reload = () => api.get("/locations").then((r) => {
    setLocations(r.data);
    return r.data;
  });
  useEffect(() => {
    reload();
  }, []);
  return { locations, reload };
};

const NewLocation = ({ testId, onCreated, onCancel }) => {
  const [name, setName] = useState("");
  const save = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/locations", { name: name.trim() });
      toast.success(`Location "${data.name}" created`);
      onCreated(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="flex gap-2 mt-2" data-testid={`${testId}-new-form`}>
      <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }} placeholder="New location, e.g. A1" className="h-9" data-testid={`${testId}-new-input`} />
      <Button type="button" size="sm" onClick={save} className="h-9 bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid={`${testId}-new-save`}>Add</Button>
      <Button type="button" size="sm" variant="ghost" onClick={onCancel} className="h-9" data-testid={`${testId}-new-cancel`}>Cancel</Button>
    </div>
  );
};

export const LocationSelect = ({ value, onChange, testId, allowAll = false, allowCreate = false, allowPending = false, placeholder = "Choose a location", className = "" }) => {
  const { locations, reload } = useLocations();
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const canCreate = allowCreate && user?.role === "owner";
  const pick = (v) => {
    if (!v) return;
    if (v === NEW) setAdding(true);
    else onChange(v === "all" ? "" : v);
  };
  const created = async (loc) => {
    const list = await reload();
    setAdding(false);
    const found = list.find((l) => l.name === loc.name);
    if (found) onChange(found.id);
  };
  return (
    <div>
      <Select value={value || (allowAll ? "all" : "")} onValueChange={pick}>
        <SelectTrigger className={className} data-testid={testId}><SelectValue placeholder={locations.length || !canCreate ? placeholder : "No locations yet — add one"} /></SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="all">All locations</SelectItem>}
          {allowAll && <SelectItem value="unassigned" data-testid={`${testId}-option-unassigned`}>Unassigned</SelectItem>}
          {allowPending && <SelectItem value="pending" className="text-[#E5A93C]" data-testid={`${testId}-option-pending`}>Pending — not yet arrived</SelectItem>}
          {locations.map((l) => (
            <SelectItem key={l.id} value={l.id} data-testid={`${testId}-option-${l.id}`}>{l.name}</SelectItem>
          ))}
          {canCreate && <SelectItem value={NEW} className="text-[#D4AF37]" data-testid={`${testId}-option-new`}>+ New location…</SelectItem>}
        </SelectContent>
      </Select>
      {adding && <NewLocation testId={testId} onCreated={created} onCancel={() => setAdding(false)} />}
    </div>
  );
};
